import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { EMPTY, finalize, map, Observable, of, switchMap, take, tap } from 'rxjs';
import {
  ActionModalComponent,
  ALERT_PERMISSIONS_TOKEN,
  AlertPermissionsProvider,
  CoordinatesModel,
  GeocodingService,
  LOADING_TOKEN,
  LoadingProvider,
  LocationAccessService,
  MODAL_TOKEN,
  ModalProvider,
  NAVIGATION_TOKEN,
  NavigationProvider,
  SelectedLocation,
  TOAST_TOKEN,
  ToastProvider
} from '@core';
import {
  LocationPickerModalComponent,
  LocationPickerResult,
  SelectableSingleRadioModalComponent
} from '@ui-components';
import { AlertsLocationStorage } from '@notifications/infra';
import {
  GetAlertsSettingsQueryHandler,
  GetAlertsSettingsQueryResult,
  UpdateAlertsSettingsCommandHandler
} from '../../../application/handlers';
import {
  availableAlertDistanceConst,
  DEFAULT_ALERTS_SETTINGS,
  hasValidAlertCoordinates,
  sanitizeAlertLocation
} from '../../../application/utils';

@Injectable()
export class AlertSettingsViewService {
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly toastProvider: ToastProvider = inject(TOAST_TOKEN);
  private readonly loadingProvider: LoadingProvider = inject(LOADING_TOKEN);
  private readonly getAlertsSettingsQueryHandler: GetAlertsSettingsQueryHandler = inject(GetAlertsSettingsQueryHandler);
  private readonly updateAlertsSettingsCommandHandler: UpdateAlertsSettingsCommandHandler = inject(
    UpdateAlertsSettingsCommandHandler
  );
  private readonly navigationProvider: NavigationProvider = inject(NAVIGATION_TOKEN);
  private readonly alertPermissionsProvider: AlertPermissionsProvider = inject(ALERT_PERMISSIONS_TOKEN);
  private readonly alertsLocationStorage: AlertsLocationStorage = inject(AlertsLocationStorage);
  private readonly locationAccessService: LocationAccessService = inject(LocationAccessService);
  private readonly geocodingService: GeocodingService = inject(GeocodingService);

  private readonly defaultValues: GetAlertsSettingsQueryResult = DEFAULT_ALERTS_SETTINGS;

  readonly notificationsValues: WritableSignal<GetAlertsSettingsQueryResult> = signal<GetAlertsSettingsQueryResult>(
    this.defaultValues
  );

  private readonly savedValues: WritableSignal<GetAlertsSettingsQueryResult> = signal<GetAlertsSettingsQueryResult>(
    this.defaultValues
  );

  readonly selectedLocation: WritableSignal<SelectedLocation | null> = signal<SelectedLocation | null>(
    sanitizeAlertLocation(this.alertsLocationStorage.location)
  );

  private readonly savedLocation: WritableSignal<SelectedLocation | null> = signal<SelectedLocation | null>(
    sanitizeAlertLocation(this.alertsLocationStorage.location)
  );

  readonly isDirty: Signal<boolean> = computed(
    () =>
      !this.areSettingsEqual(this.notificationsValues(), this.savedValues()) ||
      !this.areLocationsEqual(this.selectedLocation(), this.savedLocation())
  );

  readonly canSave: Signal<boolean> = computed(() => {
    if (!this.isDirty()) return false;

    return !this.notificationsValues().isEnabled || hasValidAlertCoordinates(this.selectedLocation()?.coordinates);
  });

  cancel(): void {
    if (!this.isDirty()) {
      this.navigationProvider.back();
      return;
    }

    this.modalProvider
      .showModal$({
        component: ActionModalComponent,
        cssClass: 'present-modal',
        componentProps: {
          header: 'Niezapisane zmiany',
          message: 'Masz niezapisane zmiany. Czy na pewno chcesz wyjść bez zapisywania?',
          btnOk: 'Wyjdź',
          btnCancel: 'Wróć do edycji',
          action: () => this.navigationProvider.back()
        }
      })
      .pipe(take(1))
      .subscribe();
  }

  loadSettings(): void {
    this.getAlertsSettingsQueryHandler
      .getAlertsSettings()
      .pipe(
        take(1),
        switchMap((settings: GetAlertsSettingsQueryResult) => this.seedLocationFromBackend(settings)),
        tap((settings: GetAlertsSettingsQueryResult) => {
          const values: GetAlertsSettingsQueryResult = settings ?? this.defaultValues;
          this.notificationsValues.set(values);
          this.savedValues.set(values);
          this.savedLocation.set(this.readStoredLocation());
          this.selectedLocation.set(this.readStoredLocation());
        })
      )
      .subscribe();
  }

  private readStoredLocation(): SelectedLocation | null {
    const stored: SelectedLocation | null = this.alertsLocationStorage.location;
    const valid: SelectedLocation | null = sanitizeAlertLocation(stored);

    if (stored && !valid) this.alertsLocationStorage.clearLocation();

    return valid;
  }

  // Backend alertów trzyma tylko współrzędne (bez nazwy). Gdy lokalny magazyn alertów jest pusty,
  // odtwarzamy nazwę przez reverse-geocode i zapisujemy pełny SelectedLocation lokalnie.
  private seedLocationFromBackend(settings: GetAlertsSettingsQueryResult): Observable<GetAlertsSettingsQueryResult> {
    const coordinates: CoordinatesModel | null = settings?.coordinates ?? null;

    if (this.readStoredLocation() || !hasValidAlertCoordinates(coordinates)) {
      return of(settings);
    }

    return this.geocodingService.reverseGeocode(coordinates).pipe(
      take(1),
      switchMap((name: string | null) =>
        this.alertsLocationStorage.setLocation({
          name: name ?? 'Moja lokalizacja',
          coordinates,
          source: 'auto'
        })
      ),
      map(() => settings)
    );
  }

  private areSettingsEqual(a: GetAlertsSettingsQueryResult, b: GetAlertsSettingsQueryResult): boolean {
    return (
      a.isEnabled === b.isEnabled &&
      a.maxDistanceMeters === b.maxDistanceMeters &&
      a.time?.hoursMin === b.time?.hoursMin &&
      a.time?.hoursMax === b.time?.hoursMax &&
      this.areSameSet(a.gameLevel, b.gameLevel) &&
      this.areSameSet(a.specificWeekdays, b.specificWeekdays)
    );
  }

  private areSameSet<T>(a: T[] = [], b: T[] = []): boolean {
    return a.length === b.length && a.every((item: T) => b.includes(item));
  }

  private areLocationsEqual(a: SelectedLocation | null, b: SelectedLocation | null): boolean {
    if (!a || !b) return a === b;

    return (
      a.name === b.name &&
      a.coordinates?.latitude === b.coordinates?.latitude &&
      a.coordinates?.longitude === b.coordinates?.longitude
    );
  }

  saveSettings(): void {
    this.alertPermissionsProvider
      .ensureNotificationPermission$()
      .pipe(
        take(1),
        switchMap((granted: boolean) => {
          if (!granted) return EMPTY;

          const currentValues: GetAlertsSettingsQueryResult = this.notificationsValues();

          this.loadingProvider.showLoading$();

          return this.updateAlertsSettingsCommandHandler
            .updateAlertsSettings({
              ...currentValues,
              coordinates: this.selectedLocation()?.coordinates ?? null
            })
            .pipe(
              take(1),
              finalize(() => this.loadingProvider.dismissLoading$())
            );
        })
      )
      .subscribe({
        next: () => {
          this.commitSelectedLocation();

          this.toastProvider.showToast({
            message: 'Zapisano zmiany'
          });

          this.loadSettings();
          this.navigationProvider.back();
        }
      });
  }

  private commitSelectedLocation(): void {
    const selected: SelectedLocation | null = this.selectedLocation();

    if (!selected) {
      this.alertsLocationStorage.clearLocation();
      return;
    }

    this.alertsLocationStorage.setLocation(selected).pipe(take(1)).subscribe();
  }

  clearLocation(): void {
    this.selectedLocation.set(null);
  }

  toggleNotifications(event: CustomEvent): void {
    const value: boolean = event.detail.checked;
    const toggle: HTMLIonToggleElement = event.target as HTMLIonToggleElement;

    this.alertPermissionsProvider
      .ensureNotificationPermission$()
      .pipe(
        take(1),
        switchMap((granted: boolean) => {
          if (granted) {
            this.notificationsValues.update((values: GetAlertsSettingsQueryResult) => ({
              ...values,
              isEnabled: value
            }));
          } else {
            toggle.checked = !value;
          }
          return EMPTY;
        })
      )
      .subscribe();
  }

  openLocationPicker(): void {
    this.modalProvider
      .showModal$<LocationPickerResult>({
        component: LocationPickerModalComponent,
        cssClass: 'location-picker-modal',
        initialBreakpoint: 1,
        breakpoints: [0, 1],
        componentProps: { selected: this.selectedLocation() }
      })
      .pipe(take(1))
      .subscribe((result: LocationPickerResult | undefined) => {
        if (!result) return;

        if (result.kind === 'auto') {
          this.locationAccessService
            .ensureLocation$()
            .pipe(take(1))
            .subscribe((acquired: SelectedLocation | null) => {
              if (acquired) this.selectedLocation.set(acquired);
            });
          return;
        }

        this.selectedLocation.set(result.location);
      });
  }

  openDistance(): void {
    if (!this.selectedLocation()) return;

    this.modalProvider
      .showModal$<number>({
        component: SelectableSingleRadioModalComponent,
        cssClass: 'present-modal',
        componentProps: {
          buttonText: 'Wybierz',
          header: 'Odległość',
          providedData: availableAlertDistanceConst,
          selectedData: this.notificationsValues().maxDistanceMeters
        }
      })
      .pipe(take(1))
      .subscribe((value: number | undefined) => {
        if (value === undefined || value === null) return;

        this.notificationsValues.update((values: GetAlertsSettingsQueryResult) => ({
          ...values,
          maxDistanceMeters: value
        }));
      });
  }
}
